// Miet- und Sondereigentumsverwaltung (Handoff Eigentümer §7, E6): monatliche Abrechnung,
// Verwaltervertrag, Kündigungstermin, Anlage V (Soll) und Mieter. Reine Funktionen – testbar.

import { eur } from '../immobilien/format';
import { mieteAktuell } from '../immobilien/einheitDetail';
import { mieterName } from '../immobilien/objektDetail';
import { istVerwaltet, istWegUns, type RollenWerte, type Zuordnung } from './rollen';
import type { MieterRoh } from './liste';

export interface AbrechnungsZeile { label: string; betrag: number; art: 'plus' | 'minus' | 'auszahlung' }

/**
 * „Monatliche Abrechnung“: Mieteinnahmen − Hausgeld (an unsere WEG bzw. den Fremdverwalter)
 * − Verwaltervergütung − Instandhaltungsrücklage (MV) = Auszahlung.
 */
export function abrechnungsZeilen(p: {
  w: Pick<RollenWerte, 'miete' | 'hgSevUns' | 'hgExt' | 'vergM' | 'nVerwaltet' | 'instM' | 'ausz'>;
  mieterAnzahl: number;
  wegNamen: readonly string[];
  fremdverwalter: readonly string[];
  verguetungJeEinheit: number | null;
  empfaenger: string;
}): AbrechnungsZeile[] {
  const { w } = p;
  const z: AbrechnungsZeile[] = [{ label: `Mieteinnahmen (${p.mieterAnzahl} ${p.mieterAnzahl === 1 ? 'Mieter' : 'Mieter'})`, betrag: w.miete, art: 'plus' }];
  if (w.hgSevUns) z.push({ label: `Hausgeld an ${p.wegNamen.map(n => (/^weg\b/i.test(n) ? n : `WEG ${n}`)).join(', ') || 'die WEG'}`, betrag: w.hgSevUns, art: 'minus' });
  if (w.hgExt) z.push({ label: `Hausgeld an ${p.fremdverwalter.join(', ') || 'den Fremdverwalter'}`, betrag: w.hgExt, art: 'minus' });
  if (w.vergM !== null && w.vergM > 0) {
    z.push({ label: `Verwaltervergütung (${w.nVerwaltet} × ${eur(p.verguetungJeEinheit ?? 0, { nachkomma: 0 })})`, betrag: w.vergM, art: 'minus' });
  }
  if (w.instM) z.push({ label: 'Instandhaltungsrücklage (5 %)', betrag: w.instM, art: 'minus' });
  z.push({ label: `Auszahlung an ${p.empfaenger}`, betrag: w.ausz, art: 'auszahlung' });
  return z;
}

/** „1 Einheit in Wohnpark Elsterblick“ / „8 Einheiten in Alte Straße 17, Am Stadtgraben 27“ */
export function umfangText(zuordnungen: readonly Zuordnung[]): string {
  const verwaltet = zuordnungen.filter(z => istVerwaltet(z.rolle));
  const n = verwaltet.reduce((s, z) => s + z.einheiten.length, 0);
  const objekte = [...new Set(verwaltet.map(z => z.objektName))];
  if (objekte.length === 0) return '–';
  return `${n} ${n === 1 ? 'Einheit' : 'Einheiten'} in ${objekte.join(', ')}`;
}

export type KuendigungZum = 'monatsende' | 'quartalsende' | 'jahresende';
export const KUENDIGUNG_ZUM: readonly { key: KuendigungZum; label: string }[] = [
  { key: 'monatsende', label: 'Monatsende' }, { key: 'quartalsende', label: 'Quartalsende' }, { key: 'jahresende', label: 'Jahresende' },
];
export const istKuendigungZum = (v: unknown): v is KuendigungZum => v === 'monatsende' || v === 'quartalsende' || v === 'jahresende';
/** Standard laut Prototyp, solange am Vertrag nichts hinterlegt ist. */
export const STANDARD_FRIST = { monate: 3, zum: 'quartalsende' as KuendigungZum };

export function fristAus(monate: unknown, zum: unknown): { monate: number; zum: KuendigungZum; standard: boolean } {
  const m = typeof monate === 'number' ? monate : typeof monate === 'string' && monate.trim() ? Number(monate) : NaN;
  if (!Number.isFinite(m) || !istKuendigungZum(zum)) return { ...STANDARD_FRIST, standard: true };
  return { monate: Math.max(0, Math.round(m)), zum, standard: false };
}

/** „3 Monate zum Quartalsende“ */
export function fristText(f: { monate: number; zum: KuendigungZum }): string {
  const zum = KUENDIGUNG_ZUM.find(k => k.key === f.zum)?.label ?? '';
  return f.monate === 0 ? `zum ${zum}` : `${f.monate} ${f.monate === 1 ? 'Monat' : 'Monate'} zum ${zum}`;
}

/** Frühester Kündigungstermin: Frist ab heute, dann Ende des Monats/Quartals/Jahres. */
export function kuendigungsTermin(heute: string, monateFrist: number, zum: KuendigungZum): string {
  const [j, m, t] = heute.split('-').map(Number) as [number, number, number];
  const ziel = new Date(Date.UTC(j, m - 1 + monateFrist, 1));
  const tageZiel = new Date(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0)).getUTCDate();
  ziel.setUTCDate(Math.min(t, tageZiel));
  const monat = ziel.getUTCMonth();
  const endMonat = zum === 'monatsende' ? monat : zum === 'quartalsende' ? Math.floor(monat / 3) * 3 + 2 : 11;
  return new Date(Date.UTC(ziel.getUTCFullYear(), endMonat + 1, 0)).toISOString().slice(0, 10);
}

/** Kündigung „3 Monate zum Quartalsende“ (Standard). */
export function quartalsende(heute: string, monateFrist = 3): string {
  return kuendigungsTermin(heute, monateFrist, 'quartalsende');
}

// ── Vergütungshistorie (eigentuemer_verguetung) ────────────────────────────────

export interface VerguetungEintrag { betrag: number; ab: string }

/** Heute gültige Vergütung: letzter Eintrag mit „gültig ab“ ≤ heute; sonst der gespeicherte Wert. */
export function aktuelleVerguetung(verlauf: readonly VerguetungEintrag[], heute: string, ersatz: number | null): number | null {
  const gueltig = verlauf.filter(v => v.ab <= heute).sort((a, b) => b.ab.localeCompare(a.ab))[0];
  return gueltig ? gueltig.betrag : ersatz;
}

/** Nächste geplante Änderung (gültig ab nach heute). */
export function geplanteVerguetung(verlauf: readonly VerguetungEintrag[], heute: string): VerguetungEintrag | null {
  return verlauf.filter(v => v.ab > heute).sort((a, b) => a.ab.localeCompare(b.ab))[0] ?? null;
}

/** Erster Tag des nächsten Monats – Vorschlag für „gültig ab“. */
export function naechsterMonatsanfang(heute: string): string {
  const [j, m] = heute.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(j, m, 1)).toISOString().slice(0, 10);
}

// ── Freigaben (eigentuemer_freigaben) ──────────────────────────────────────────

export type FreigabeStatus = 'offen' | 'freigegeben' | 'abgelehnt';
export interface FreigabeKurz { id: string; status: FreigabeStatus; betrag: number | null }

export function freigabeKennzahlen(f: readonly FreigabeKurz[]): { offen: number; volumen: number } {
  const offen = f.filter(x => x.status === 'offen');
  return { offen: offen.length, volumen: Math.round(offen.reduce((s, x) => s + (x.betrag ?? 0), 0) * 100) / 100 };
}

/** Innerhalb der Wertgrenze ist keine Rückfrage nötig – die Freigabe gilt sofort. */
export function innerhalbWertgrenze(betrag: number | null, wertgrenze: number | null): boolean {
  return betrag !== null && wertgrenze !== null && betrag <= wertgrenze;
}

export interface AnlageVZeile { key: string; titel: string; einnahmen: number; hausgeld: number }

/** Anlage V je verwaltetem Objekt: Soll-Mieteinnahmen (12 × Monatsmiete), dazu das Hausgeld der Einheiten. */
export function anlageVZeilen(zuordnungen: readonly Zuordnung[]): AnlageVZeile[] {
  return zuordnungen.filter(z => istVerwaltet(z.rolle)).map(z => ({
    key: `${z.objektId}|${z.rolle}`,
    titel: z.ganzesObjekt ? z.objektName : `${z.objektName} · ${z.einheiten.map(e => e.bezeichnung).join(', ')}`,
    einnahmen: Math.round(z.einheiten.reduce((s, e) => s + e.miete, 0) * 12),
    hausgeld: Math.round(z.einheiten.reduce((s, e) => s + e.hausgeld, 0) * 12),
  }));
}

export interface MieterZeile { id: string; name: string; ort: string; kalt: number; einheitId: string }

/** Mieter in den von uns verwalteten Einheiten (MV/SEV), mit Kaltmiete. */
export function mieterZeilen(zuordnungen: readonly Zuordnung[], mieterJeEinheit: ReadonlyMap<string, readonly MieterRoh[]>, heute: string): MieterZeile[] {
  return zuordnungen.filter(z => istVerwaltet(z.rolle)).flatMap(z => z.einheiten.flatMap(e => (mieterJeEinheit.get(e.id) ?? []).map(m => ({
    id: m.id,
    name: mieterName(m),
    ort: [z.objektName, e.bezeichnung, z.rolle === 'MV' ? '' : 'SEV'].filter(Boolean).join(' · '),
    kalt: mieteAktuell([m], heute).kalt,
    einheitId: e.id,
  }))));
}

/** WEG-Objekte (unsere), in denen Hausgeld aus der SEV-Miete verrechnet wird. */
export function wegNamenMitSev(zuordnungen: readonly Zuordnung[]): string[] {
  return [...new Set(zuordnungen.filter(z => z.rolle === 'WEG+SEV').map(z => z.objektName))];
}

export const istWegMitglied = (zuordnungen: readonly Zuordnung[]): boolean => zuordnungen.some(z => istWegUns(z.rolle));
